import { C, type Hex, mix, type Pix } from './pix';

/**
 * Shared pieces for icon pictures, on the 32×32 grid: weapons, hands, qi shapes and small
 * figures. Pictures are drawn flat; the composer lights them from the upper right and outlines
 * them, so a piece only needs its main colours and the odd white glint.
 */

export type Pt = readonly [number, number];

/**
 * Pixel art from rows of characters, each a colour in `cols` ('.' or ' ' is left clear).
 * `flip` mirrors it left to right; `k` draws every pixel as a k×k block.
 */
export function sprite(
  p: Pix,
  x: number,
  y: number,
  rows: readonly string[],
  cols: Readonly<Record<string, Hex>>,
  o: { flip?: boolean; flipY?: boolean; k?: number } = {},
): void {
  const k = o.k ?? 1;
  const w = Math.max(...rows.map((r) => r.length));
  rows.forEach((row, j) => {
    for (let i = 0; i < row.length; i++) {
      const c = cols[row[i]!];
      if (!c) continue;
      const xi = o.flip ? w - 1 - i : i;
      const yj = o.flipY ? rows.length - 1 - j : j;
      p.rect(x + xi * k, y + yj * k, k, k, c);
    }
  });
}

const unit = (x0: number, y0: number, x1: number, y1: number) => {
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  return { dx: (x1 - x0) / len, dy: (y1 - y0) / len, len };
};

/**
 * A blade from its guard (x0,y0) to its point (x1,y1) as a filled shape: `w` wide at the back,
 * tapering to the point, bent sideways by `curve` (a sabre). Its cutting edge gets a white line.
 */
export function blade(
  p: Pix,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  o: { w?: number; curve?: number; col?: Hex; edge?: Hex; taper?: number } = {},
): void {
  const { dx, dy, len } = unit(x0, y0, x1, y1);
  const nx = -dy;
  const ny = dx;
  const w = o.w ?? 2;
  const curve = o.curve ?? 0;
  const taper = o.taper ?? 0.75;
  const back: Pt[] = [];
  const edge: Pt[] = [];
  const N = Math.max(6, Math.ceil(len));
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const bend = curve * Math.sin(t * Math.PI * 0.85);
    const cx = x0 + dx * len * t + nx * bend;
    const cy = y0 + dy * len * t + ny * bend;
    const half = (w / 2) * (t < taper ? 1 : Math.max(0, (1 - t) / (1 - taper)));
    back.push([cx + nx * half, cy + ny * half]);
    edge.push([cx - nx * half, cy - ny * half]);
  }
  p.poly([...back, ...edge.reverse()], o.col ?? C.steel);
  if (o.edge !== undefined || w >= 3) p.path(edge.reverse().slice(1, -1), o.edge ?? C.white);
}

/**
 * A straight sword (jian): pommel at (x0,y0), point at (x1,y1), with a crossguard, wrapped grip
 * and an optional tassel.
 */
export function sword(
  p: Pix,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  o: { hilt?: Hex; blade?: Hex; grip?: number; tassel?: Hex; w?: number; guard?: number } = {},
): void {
  const { dx, dy } = unit(x0, y0, x1, y1);
  const nx = -dy;
  const ny = dx;
  const g = o.grip ?? 5;
  const gx = x0 + dx * g;
  const gy = y0 + dy * g;
  blade(p, gx, gy, x1, y1, { w: o.w ?? 2.2, col: o.blade ?? C.steel, taper: 0.85 });
  p.thick(x0, y0, gx, gy, C.woodDark, 2);
  const hilt = o.hilt ?? C.gold;
  const gw = o.guard ?? 3;
  p.thick(gx - nx * gw, gy - ny * gw, gx + nx * gw, gy + ny * gw, hilt, 2);
  p.rect(Math.round(x0 - dx * 1.5) - 1, Math.round(y0 - dy * 1.5) - 1, 2, 2, hilt);
  if (o.tassel) {
    const tx = Math.round(x0 - dx * 2);
    const ty = Math.round(y0 - dy * 2);
    p.line(tx, ty, tx - 1, ty + 4, o.tassel).line(tx + 1, ty, tx, ty + 4, o.tassel);
  }
}

/** A broad single-edged sabre (dao): grip end (x0,y0), point (x1,y1), curving to its back. */
export function sabre(
  p: Pix,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  o: { hilt?: Hex; blade?: Hex; curve?: number; w?: number; edge?: Hex } = {},
): void {
  const { dx, dy } = unit(x0, y0, x1, y1);
  const nx = -dy;
  const ny = dx;
  const gx = x0 + dx * 5;
  const gy = y0 + dy * 5;
  blade(p, gx, gy, x1, y1, {
    w: o.w ?? 3.6,
    curve: o.curve ?? -2.2,
    col: o.blade ?? C.steel,
    taper: 0.7,
    edge: o.edge ?? C.white,
  });
  p.thick(x0, y0, gx, gy, C.woodDark, 2);
  const hilt = o.hilt ?? C.gold;
  p.ell(gx, gy, 2.2, 2.2, hilt);
  p.px(gx + nx, gy + ny, mix(hilt, C.white, 0.5));
}

/** An open palm, fingers up (13×13), at (x,y), `k` times its size. */
export function palm(
  p: Pix,
  x: number,
  y: number,
  col: Hex,
  o: { k?: number; flip?: boolean; flipY?: boolean; lines?: Hex } = {},
): void {
  sprite(
    p,
    x,
    y,
    [
      '.....ss......',
      '.....ss.ss...',
      '..ss.ss.ss...',
      '..ss.ss.ss.ss',
      '..ss.ss.ss.ss',
      '..ss.ss.ss.ss',
      '..sssssssssss',
      'ss.ssssssssss',
      'sssssssssssss',
      '.sssssssssss.',
      '..ssssssssss.',
      '...ssssssss..',
      '....ssssss...',
    ],
    { s: col },
    o,
  );
  if (o.lines) {
    const k = o.k ?? 1;
    const fx = (i: number) => (o.flip ? x + (12 - i) * k : x + i * k);
    const ly = o.flipY ? y + 6 * k : y + 3 * k;
    for (const i of [4, 7, 10]) p.rect(fx(i), ly, k, 4 * k, o.lines);
  }
}

/** A clenched fist, knuckles forward (12×11). */
export function fist(
  p: Pix,
  x: number,
  y: number,
  col: Hex,
  o: { k?: number; flip?: boolean; flipY?: boolean; shade?: Hex } = {},
): void {
  sprite(
    p,
    x,
    y,
    [
      '..sss.sss...',
      '.ssssssssss.',
      'sssssssssss.',
      'sssssssssssd',
      'dsssssssssss',
      'ssddddddddss',
      'ssssssssssss',
      'ssssssssssss',
      '.ssssssssss.',
      '..ssssssss..',
      '...ssssss...',
    ],
    { s: col, d: o.shade ?? mix(col, C.ink, 0.35) },
    o,
  );
}

/** A clawed hand, three hooked talons raking down (14×13). */
export function claw(p: Pix, x: number, y: number, col: Hex, nail: Hex, o: { flip?: boolean } = {}): void {
  sprite(
    p,
    x,
    y,
    [
      '..ssssssss....',
      '.ssssssssss...',
      'ssssssssssss..',
      'ssssssssssss..',
      '.s..s...s..s..',
      '.s..s...s..ss.',
      '.n..n...n...n.',
      '.n..n...n...n.',
      'nn.nn..nn..nn.',
      'n..n...n...n..',
    ],
    { s: col, n: nail },
    o,
  );
}

/** A zigzag lightning bolt through the points, `w` wide, with a white core. */
export function bolt(p: Pix, pts: readonly Pt[], col: Hex, w = 3): void {
  p.path(pts, col, w);
  p.path(pts, C.white, 1);
}

/** A crescent of light (a cut) round (cx,cy) from angle a0 to a1, thickest in the middle. */
export function slash(
  p: Pix,
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  col: Hex,
  w = 3,
): void {
  const steps = Math.ceil(Math.abs(a1 - a0) * r * 2);
  for (let i = 0; i <= steps; i++) {
    const k = i / steps;
    const a = a0 + (a1 - a0) * k;
    const t = Math.round(Math.sin(k * Math.PI) * (w - 0.01) + 0.5);
    for (let q = 0; q < t; q++)
      p.px(cx + Math.cos(a) * (r - q), cy + Math.sin(a) * (r - q), q === 0 ? C.white : col);
  }
}

/** Speed lines trailing to the left of `x`, at the given rows. */
export function speed(p: Pix, x: number, rows: readonly (readonly [number, number])[], col: Hex): void {
  for (const [y, l] of rows) p.rect(x - l, y, l, 1, col);
}

/** Spiral arms of light round a bright core. */
export function swirl(
  p: Pix,
  cx: number,
  cy: number,
  r: number,
  col: Hex,
  o: { arms?: number; turn?: number; squash?: number; core?: Hex } = {},
): void {
  const arms = o.arms ?? 3;
  const turn = o.turn ?? 4.2;
  for (let arm = 0; arm < arms; arm++)
    for (let t = 0; t < 1; t += 0.015) {
      const a = (arm * Math.PI * 2) / arms + t * turn;
      const rr = 2 + t * (r - 2);
      p.px(
        cx + Math.cos(a) * rr,
        cy + Math.sin(a) * rr * (o.squash ?? 1),
        t > 0.55 ? col : mix(col, C.white, 0.5),
      );
      if (t > 0.3) p.px(cx + Math.cos(a + 0.12) * rr, cy + Math.sin(a + 0.12) * rr * (o.squash ?? 1), col);
    }
  if (o.core !== undefined) p.ell(cx, cy, 2, 2, o.core);
}

/** A six-armed snowflake. */
export function snowflake(p: Pix, cx: number, cy: number, r: number, col: Hex = C.ice, tips = true): void {
  for (let k = 0; k < 3; k++) {
    const a = (k * Math.PI) / 3 + Math.PI / 2;
    p.line(cx - Math.cos(a) * r, cy - Math.sin(a) * r, cx + Math.cos(a) * r, cy + Math.sin(a) * r, col);
    if (tips && r >= 4)
      for (const s of [-1, 1]) {
        const ex = cx + s * Math.cos(a) * r * 0.65;
        const ey = cy + s * Math.sin(a) * r * 0.65;
        p.line(ex, ey, ex + Math.cos(a + s * 2.4) * 2, ey + Math.sin(a + s * 2.4) * 2, col);
        p.line(ex, ey, ex + Math.cos(a - s * 2.4) * 2, ey + Math.sin(a - s * 2.4) * 2, col);
      }
  }
  p.px(cx, cy, C.white);
}

/** A falling drop (blood, poison, dew), point up. */
export function drop(p: Pix, x: number, y: number, r: number, col: Hex): void {
  p.ell(x, y, r, r * 1.1, col);
  p.poly(
    [
      [x - r * 0.7, y - r * 0.4],
      [x, y - r * 2.4],
      [x + r * 0.7, y - r * 0.4],
    ],
    col,
  );
  p.px(x - r * 0.35, y - r * 0.2, mix(col, C.white, 0.6));
}

/** A puffy cloud `w` wide, its base at (x,y). */
export function cloud(p: Pix, x: number, y: number, w: number, col: Hex): void {
  const r = w / 4.5;
  p.rect(x, y - r, w, r + 1, col);
  p.ell(x + r, y - r, r, r, col);
  p.ell(x + w * 0.45, y - r * 1.5, r * 1.35, r * 1.35, col);
  p.ell(x + w - r, y - r * 1.1, r * 1.05, r * 1.05, col);
}

/** A curling gust: a line that ends in a small spiral. */
export function gust(p: Pix, x: number, y: number, len: number, col: Hex, up = true): void {
  p.line(x, y, x + len, y, col);
  const s = up ? -1 : 1;
  p.ring(
    x + len,
    y + s * 2,
    2,
    2,
    col,
    up ? -Math.PI / 2 - Math.PI : Math.PI / 2 - Math.PI,
    up ? Math.PI / 2 - Math.PI : Math.PI * 1.5 - Math.PI,
  );
}

/** A small running/standing figure (7×11). */
export function figure(
  p: Pix,
  x: number,
  y: number,
  col: Hex,
  pose: 'run' | 'stand' | 'leap' | 'sit' = 'run',
  o: { flip?: boolean; head?: Hex } = {},
): void {
  const rows: Record<typeof pose, string[]> = {
    run: ['...hh..', '...hh..', '..bbbb.', '.b.bb.b', 'b..bb..', '...bbb.', '..b..b.', '.b...b.', 'b.....b'],
    stand: [
      '..hh...',
      '..hh...',
      '.bbbb..',
      'b.bb.b.',
      'b.bb.b.',
      '..bb...',
      '.b..b..',
      '.b..b..',
      '.b..b..',
    ],
    leap: ['....hh.', '....hh.', 'b.bbbb.', '.b.bb.b', '...bb..', '..bbb..', '.b...bb', 'b......'],
    sit: ['..hh...', '..hh...', '.bbbb..', 'b.bb.b.', '.bbbb..', 'bbbbbb.'],
  };
  sprite(p, x, y, rows[pose], { h: o.head ?? col, b: col }, o);
}

/** A ring of dots round a point (a target, a seal). */
export function target(p: Pix, cx: number, cy: number, r: number, col: Hex): void {
  p.ring(cx, cy, r, r, col);
  p.px(cx, cy, C.white);
  p.px(cx - r - 1, cy, col)
    .px(cx + r + 1, cy, col)
    .px(cx, cy - r - 1, col)
    .px(cx, cy + r + 1, col);
}

/** A four-pointed glint. */
export function star(p: Pix, cx: number, cy: number, r: number, col: Hex = C.white): void {
  p.line(cx - r, cy, cx + r, cy, col).line(cx, cy - r, cx, cy + r, col);
  if (r >= 3)
    p.px(cx - 1, cy - 1, col)
      .px(cx + 1, cy - 1, col)
      .px(cx - 1, cy + 1, col)
      .px(cx + 1, cy + 1, col);
}

/** A five-petalled blossom. */
export function flower(p: Pix, cx: number, cy: number, r: number, col: Hex, heart: Hex = C.gold): void {
  for (let k = 0; k < 5; k++) {
    const a = -Math.PI / 2 + (k * Math.PI * 2) / 5;
    p.ell(cx + Math.cos(a) * r * 0.62, cy + Math.sin(a) * r * 0.62, r * 0.48, r * 0.48, col);
  }
  p.ell(cx, cy, Math.max(1, r * 0.28), Math.max(1, r * 0.28), heart);
}

/** A single petal drifting. */
export function petal(p: Pix, x: number, y: number, col: Hex): void {
  p.rect(x, y, 2, 1, col).rect(x + 1, y + 1, 2, 1, col);
}

/** A bronze temple bell (w wide at the lip), its crown at (cx,y). */
export function bell(
  p: Pix,
  cx: number,
  y: number,
  h: number,
  col: Hex,
  o: { w?: number; band?: Hex } = {},
): void {
  const w = o.w ?? h * 0.9;
  for (let j = 0; j < h; j++) {
    const t = j / (h - 1);
    const hw = Math.round(w * 0.28 + (w * 0.5 - w * 0.28) * t * t);
    p.rect(cx - hw, y + j, hw * 2, 1, col);
  }
  p.rect(cx - Math.round(w / 2) - 1, y + h - 2, Math.round(w) + 2, 2, mix(col, C.ink, 0.25));
  p.rect(cx - 2, y - 3, 4, 3, col);
  if (o.band) {
    p.rect(cx - Math.round(w * 0.36), y + Math.round(h * 0.45), Math.round(w * 0.72), 1, o.band);
    for (let i = -2; i <= 2; i++) p.px(cx + i * 2, y + Math.round(h * 0.25), o.band);
  }
  p.rect(cx - 1, y + h, 2, 2, mix(col, C.ink, 0.25));
}

/** A lotus: five pointed petals over a pad. */
export function lotus(
  p: Pix,
  cx: number,
  by: number,
  w: number,
  col: Hex,
  inner: Hex = mix(col, C.white, 0.45),
): void {
  const petalAt = (dx: number, h: number, c: Hex) =>
    p.poly(
      [
        [cx + dx - w * 0.14, by],
        [cx + dx, by - h],
        [cx + dx + w * 0.14, by],
      ],
      c,
    );
  petalAt(-w * 0.36, w * 0.32, col);
  petalAt(w * 0.36, w * 0.32, col);
  petalAt(-w * 0.18, w * 0.48, col);
  petalAt(w * 0.18, w * 0.48, col);
  petalAt(0, w * 0.6, inner);
  p.rect(cx - w / 2, by, w, 2, C.jade);
}

/** The yin-yang (太极) at (cx,cy). */
export function taiji(
  p: Pix,
  cx: number,
  cy: number,
  r: number,
  light: Hex = C.paper,
  dark: Hex = C.ink,
): void {
  p.ell(cx, cy, r, r, dark);
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++)
    for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const ux = x + 0.5 - cx;
      const uy = y + 0.5 - cy;
      if (ux * ux + uy * uy > r * r) continue;
      const upper = Math.hypot(ux, uy + r / 2) < r / 2;
      const lower = Math.hypot(ux, uy - r / 2) < r / 2;
      if ((ux > 0 && !lower) || upper) p.px(x, y, light);
    }
  const d = Math.max(1, Math.round(r / 5));
  p.rect(Math.round(cx - d / 2), Math.round(cy - r / 2 - d / 2), d, d, dark);
  p.rect(Math.round(cx - d / 2), Math.round(cy + r / 2 - d / 2), d, d, light);
}

/** A crescent moon. */
export function moon(p: Pix, cx: number, cy: number, r: number, col: Hex, cut: Hex | null = null): void {
  for (let y = Math.floor(cy - r); y <= Math.ceil(cy + r); y++)
    for (let x = Math.floor(cx - r); x <= Math.ceil(cx + r); x++) {
      const ux = x + 0.5 - cx;
      const uy = y + 0.5 - cy;
      if (ux * ux + uy * uy > r * r) continue;
      const vx = ux - r * 0.45;
      const vy = uy + r * 0.25;
      if (vx * vx + vy * vy < r * r * 0.72) {
        if (cut) p.px(x, y, cut);
      } else p.px(x, y, col);
    }
}

/** A heart. */
export function heart(p: Pix, cx: number, cy: number, r: number, col: Hex): void {
  p.ell(cx - r * 0.5, cy - r * 0.2, r * 0.6, r * 0.6, col);
  p.ell(cx + r * 0.5, cy - r * 0.2, r * 0.6, r * 0.6, col);
  p.poly(
    [
      [cx - r * 1.05, cy],
      [cx + r * 1.05, cy],
      [cx, cy + r],
    ],
    col,
  );
  p.px(cx - r * 0.6, cy - r * 0.45, mix(col, C.white, 0.6));
}

/** A shield face, its top centre at (cx,y). */
export function shield(p: Pix, cx: number, y: number, w: number, h: number, col: Hex, rim?: Hex): void {
  const pts: Pt[] = [
    [cx - w / 2, y],
    [cx + w / 2, y],
    [cx + w / 2, y + h * 0.55],
    [cx, y + h],
    [cx - w / 2, y + h * 0.55],
  ];
  if (rim) p.poly(pts, rim);
  const k = rim ? 1.5 : 0;
  p.poly(
    [
      [cx - w / 2 + k, y + k],
      [cx + w / 2 - k, y + k],
      [cx + w / 2 - k, y + h * 0.55],
      [cx, y + h - k * 1.4],
      [cx - w / 2 + k, y + h * 0.55],
    ],
    col,
  );
}

/** A dark crack through the points, with chips flying off its end. */
export function crack(p: Pix, pts: readonly Pt[], col: Hex = C.ink, w = 1): void {
  p.path(pts, col, w);
}

/** Licking flames, their base at (x,by), `w` wide and up to `h` tall. */
export function flames(
  p: Pix,
  x: number,
  by: number,
  w: number,
  h: number,
  col: Hex = C.fire,
  tip: Hex = C.fireLight,
): void {
  for (let i = 0; i < w; i++) {
    const t = i / Math.max(1, w - 1);
    const hh = Math.round(h * (0.45 + 0.55 * Math.abs(Math.sin(t * Math.PI * 2.5 + 0.6))));
    p.rect(x + i, by - hh, 1, hh, col);
    p.rect(x + i, by - hh, 1, Math.max(1, Math.round(hh * 0.35)), tip);
  }
}

/** A conical mountain peak with a snowcap. */
export function peak(
  p: Pix,
  cx: number,
  by: number,
  w: number,
  h: number,
  col: Hex,
  cap: Hex | null = C.paper,
): void {
  p.poly(
    [
      [cx - w / 2, by],
      [cx, by - h],
      [cx + w / 2, by],
    ],
    col,
  );
  if (cap)
    p.poly(
      [
        [cx - w * 0.14, by - h * 0.72],
        [cx, by - h],
        [cx + w * 0.14, by - h * 0.72],
      ],
      cap,
    );
}

/** A bamboo stalk from (x,y0) up to (x,y1), with nodes and a leaf or two. */
export function bamboo(p: Pix, x: number, y0: number, y1: number, col: Hex = '#6cc04a', leaves = true): void {
  p.rect(x, y1, 2, y0 - y1, col);
  for (let y = y0 - 5; y > y1; y -= 6) p.rect(x - 1, y, 4, 1, mix(col, C.ink, 0.35));
  if (leaves) {
    p.line(x + 2, y1 + 4, x + 6, y1 + 1, col).line(x + 2, y1 + 5, x + 6, y1 + 2, col);
    p.line(x - 1, y1 + 9, x - 5, y1 + 7, col).line(x - 1, y1 + 10, x - 5, y1 + 8, col);
  }
}

/** A leaf pointing along (dx,dy) from (x,y). */
export function leaf(p: Pix, x: number, y: number, dx: number, dy: number, len: number, col: Hex): void {
  const { dx: ux, dy: uy } = unit(0, 0, dx, dy);
  const nx = -uy;
  const ny = ux;
  p.poly(
    [
      [x, y],
      [x + ux * len * 0.5 + nx * len * 0.22, y + uy * len * 0.5 + ny * len * 0.22],
      [x + ux * len, y + uy * len],
      [x + ux * len * 0.5 - nx * len * 0.22, y + uy * len * 0.5 - ny * len * 0.22],
    ],
    col,
  );
}

/** A chain of links from (x0,y0) to (x1,y1). */
export function chain(p: Pix, x0: number, y0: number, x1: number, y1: number, col: Hex = C.steelMid): void {
  const { dx, dy, len } = unit(x0, y0, x1, y1);
  for (let s = 0, i = 0; s < len; s += 3.2, i++) {
    const x = x0 + dx * s;
    const y = y0 + dy * s;
    if (i % 2) p.ring(x, y, 1.4, 1.4, col);
    else p.rect(Math.round(x) - 1, Math.round(y), 3, 1, mix(col, C.white, 0.3));
  }
}

/** A calligraphy brush from its butt (x0,y0) to its tip (x1,y1). */
export function brush(p: Pix, x0: number, y0: number, x1: number, y1: number, ink: Hex = C.ink): void {
  const { dx, dy, len } = unit(x0, y0, x1, y1);
  const hx = x0 + dx * (len - 6);
  const hy = y0 + dy * (len - 6);
  p.thick(x0, y0, hx, hy, '#b0703a', 2);
  p.thick(hx - dx, hy - dy, hx, hy, C.gold, 2);
  blade(p, hx, hy, x1, y1, { w: 3.4, col: ink, taper: 0.4 });
  p.line(hx + dx, hy + dy, x1 - dx, y1 - dy, mix(ink, C.white, 0.35));
}

/** A thick ink stroke along points, `w` wide in the middle, thin at the ends. */
export function inkStroke(p: Pix, pts: readonly Pt[], w: number, col: Hex = C.ink): void {
  const total = pts.length - 1;
  for (let i = 0; i < total; i++) {
    const [a, b] = [pts[i]!, pts[i + 1]!];
    const steps = Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) * 2);
    for (let s = 0; s <= steps; s++) {
      const t = (i + s / steps) / total;
      const r = Math.max(0.6, (w / 2) * Math.sin(Math.min(1, t * 1.15 + 0.05) * Math.PI));
      p.ell(a[0] + ((b[0] - a[0]) * s) / steps, a[1] + ((b[1] - a[1]) * s) / steps, r, r, col);
    }
  }
}

/** Splashed ink spots. */
export function splats(p: Pix, list: readonly (readonly [number, number, number])[], col: Hex = C.ink): void {
  for (const [x, y, r] of list) p.ell(x, y, r, r, col);
}

/** A hanging scroll unrolled (w×h of paper with rods top and bottom). */
export function scroll(
  p: Pix,
  x: number,
  y: number,
  w: number,
  h: number,
  paper: Hex = C.paper,
  rod: Hex = C.wood,
): void {
  p.rect(x, y + 1, w, h - 2, paper);
  p.rect(x - 2, y, w + 4, 2, rod);
  p.rect(x - 2, y + h - 2, w + 4, 2, rod);
  p.px(x - 3, y, C.gold)
    .px(x + w + 2, y, C.gold)
    .px(x - 3, y + h - 2, C.gold)
    .px(x + w + 2, y + h - 2, C.gold);
}

/** A folding fan opened from its pivot (cx,cy), facing up. */
export function fan(
  p: Pix,
  cx: number,
  cy: number,
  r: number,
  col: Hex,
  rib: Hex = C.woodDark,
  a0 = -2.6,
  a1 = -0.55,
): void {
  const pts: Pt[] = [[cx, cy]];
  for (let a = a0; a <= a1 + 1e-6; a += (a1 - a0) / 12)
    pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  p.poly(pts, col);
  for (let k = 0; k <= 6; k++) {
    const a = a0 + ((a1 - a0) * k) / 6;
    p.line(cx, cy, cx + Math.cos(a) * r * 0.45, cy + Math.sin(a) * r * 0.45, rib);
  }
  p.ring(cx, cy, r, r, mix(col, C.ink, 0.3), a0, a1);
}

/** A thin needle from (x0,y0) to its point (x1,y1), with a glint. */
export function needle(
  p: Pix,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  col: Hex = C.steel,
  tip?: Hex,
): void {
  p.line(x0, y0, x1, y1, col);
  p.px(x1, y1, tip ?? C.white);
}

/** An eye, open, with a coloured iris. */
export function eye(p: Pix, cx: number, cy: number, w: number, iris: Hex, white: Hex = C.paper): void {
  p.ell(cx, cy, w / 2, w / 4.2, white);
  p.ell(cx, cy, w / 5, w / 5, iris);
  p.rect(Math.round(cx) - 1, Math.round(cy) - 1, 1, 1, C.white);
}

/** A bare footprint (sole and toes), 6×9, heel at the bottom. */
export function footprint(p: Pix, x: number, y: number, col: Hex, flip = false): void {
  sprite(
    p,
    x,
    y,
    ['.t.t.', 't.t.t', '.....', '.sss.', 'sssss', 'sssss', '.sss.', '.sss.', '.ss..'],
    { t: col, s: col },
    { flip },
  );
}

/** Concentric sound or force arcs fanning out to the right of (cx,cy). */
export function waves(
  p: Pix,
  cx: number,
  cy: number,
  r0: number,
  n: number,
  col: Hex,
  spread = 0.9,
  gap = 3,
): void {
  for (let i = 0; i < n; i++)
    p.ring(cx, cy, r0 + i * gap, r0 + i * gap, i === 0 ? mix(col, C.white, 0.5) : col, -spread, spread);
}

/** A tiny skull (7×7). */
export function skull(p: Pix, x: number, y: number, col: Hex = C.paper): void {
  sprite(p, x, y, ['.sssss.', 'sssssss', 's.s.s.s', 'sssssss', '.s.s.s.', '.sssss.'], { s: col });
}

/** A gold coin (or silver tael face) at (cx,cy). */
export function coin(p: Pix, cx: number, cy: number, r: number, col: Hex = C.gold): void {
  p.ell(cx, cy, r, r, col);
  p.ell(cx, cy, r - 1, r - 1, mix(col, C.white, 0.25));
  p.rect(Math.round(cx) - 1, Math.round(cy) - 1, 2, 2, mix(col, C.ink, 0.5));
}

/** A dotted trail (afterimage, a path left behind). */
export function dots(p: Pix, x0: number, y0: number, x1: number, y1: number, col: Hex, every = 3): void {
  const { dx, dy, len } = unit(x0, y0, x1, y1);
  for (let s = 0; s <= len; s += every) p.px(x0 + dx * s, y0 + dy * s, col);
}

/** A ground ring (a circle seen at an angle) with a lit front edge. */
export function groundRing(p: Pix, cx: number, cy: number, rx: number, ry: number, col: Hex): void {
  p.ring(cx, cy, rx, ry, col, Math.PI, Math.PI * 2);
  p.ring(cx, cy, rx, ry, mix(col, C.white, 0.5), 0, Math.PI);
}

/** Fragments flung out from a point. */
export function shards(p: Pix, cx: number, cy: number, r: number, col: Hex, n = 6, seed = 0): void {
  for (let k = 0; k < n; k++) {
    const a = seed + (k * Math.PI * 2) / n;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    p.rect(Math.round(x), Math.round(y), 2, 1, col);
    p.px(x + Math.cos(a) * 2, y + Math.sin(a) * 2, col);
  }
}
